import React, { useState, useEffect } from 'react';

const ResumeDataEditor = ({ parsedData, onSave, isFinalized }) => {
  const [editedData, setEditedData] = useState(parsedData);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    contact: true,
    experience: true,
    education: true,
    skills: true
  });

  useEffect(() => {
    setEditedData(parsedData);
    setHasChanges(false);
  }, [parsedData]);

  const handleChange = (section, field, value) => {
    setEditedData({
      ...editedData,
      [section]: {
        ...editedData[section],
        [field]: value
      }
    });
    setHasChanges(true);
  };

  const handleArrayChange = (section, index, field, value) => {
    const newArray = [...editedData[section]];
    newArray[index] = {
      ...newArray[index],
      [field]: value
    };
    setEditedData({
      ...editedData,
      [section]: newArray
    });
    setHasChanges(true);
  };

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editedData);
      setHasChanges(false);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (label, value, onChange, multiline = false) => {
    if (isFinalized) {
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
          <div className="text-gray-900 bg-gray-50 p-2 rounded border border-gray-200">
            {value || 'Not provided'}
          </div>
        </div>
      );
    }

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        {multiline ? (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        ) : (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        )}
      </div>
    );
  };

  const SectionHeader = ({ title, section, icon }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-colors"
    >
      <div className="flex items-center space-x-3">
        {icon}
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <svg
        className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections[section] ? 'transform rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Parsed Resume Data</h2>
        {isFinalized && (
          <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
            Read-only (Finalized)
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-6">
        {isFinalized
          ? 'This resume has been finalized. Upload a new version to make changes.'
          : 'Review and correct any information that was parsed incorrectly.'}
      </p>

      <div className="space-y-4">
        {/* Contact Information */}
        <div>
          <SectionHeader
            title="Contact Information"
            section="contact"
            icon={
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            }
          />
          {expandedSections.contact && editedData.contact && (
            <div className="p-4 border border-gray-200 rounded-b-lg">
              {renderField('Name', editedData.contact.name, (v) => handleChange('contact', 'name', v))}
              {renderField('Email', editedData.contact.email, (v) => handleChange('contact', 'email', v))}
              {renderField('Phone', editedData.contact.phone, (v) => handleChange('contact', 'phone', v))}
              {renderField('LinkedIn', editedData.contact.linkedin, (v) => handleChange('contact', 'linkedin', v))}
              {renderField('Location', editedData.contact.location, (v) => handleChange('contact', 'location', v))}
            </div>
          )}
        </div>

        {/* Experience */}
        <div>
          <SectionHeader
            title="Experience"
            section="experience"
            icon={
              <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            }
          />
          {expandedSections.experience && editedData.experience && editedData.experience.length > 0 && (
            <div className="p-4 border border-gray-200 rounded-b-lg space-y-6">
              {editedData.experience.map((exp, index) => (
                <div key={index} className="pb-6 border-b border-gray-200 last:border-b-0 last:pb-0">
                  <div className="flex items-center text-sm font-medium text-gray-500 mb-3">
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      Position {index + 1}
                    </span>
                  </div>
                  {renderField('Company', exp.company, (v) => handleArrayChange('experience', index, 'company', v))}
                  {renderField('Title', exp.title, (v) => handleArrayChange('experience', index, 'title', v))}
                  <div className="grid grid-cols-2 gap-4">
                    {renderField('Start Date', exp.start_date, (v) => handleArrayChange('experience', index, 'start_date', v))}
                    {renderField('End Date', exp.end_date, (v) => handleArrayChange('experience', index, 'end_date', v))}
                  </div>
                  {renderField('Location', exp.location, (v) => handleArrayChange('experience', index, 'location', v))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Education */}
        <div>
          <SectionHeader
            title="Education"
            section="education"
            icon={
              <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              </svg>
            }
          />
          {expandedSections.education && editedData.education && editedData.education.length > 0 && (
            <div className="p-4 border border-gray-200 rounded-b-lg space-y-6">
              {editedData.education.map((edu, index) => (
                <div key={index} className="pb-6 border-b border-gray-200 last:border-b-0 last:pb-0">
                  <div className="flex items-center text-sm font-medium text-gray-500 mb-3">
                    <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                      Degree {index + 1}
                    </span>
                  </div>
                  {renderField('Institution', edu.institution, (v) => handleArrayChange('education', index, 'institution', v))}
                  {renderField('Degree', edu.degree, (v) => handleArrayChange('education', index, 'degree', v))}
                  {renderField('Field of Study', edu.field, (v) => handleArrayChange('education', index, 'field', v))}
                  {renderField('GPA', edu.gpa, (v) => handleArrayChange('education', index, 'gpa', v))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Skills */}
        <div>
          <SectionHeader
            title="Skills"
            section="skills"
            icon={
              <svg className="w-5 h-5 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 7H7v6h6V7z" />
                <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
              </svg>
            }
          />
          {expandedSections.skills && editedData.skills && (
            <div className="p-4 border border-gray-200 rounded-b-lg">
              {renderField('Technical Skills', editedData.skills.technical?.join(', '),
                (v) => handleChange('skills', 'technical', v.split(',').map(s => s.trim())), true)}
              {renderField('Soft Skills', editedData.skills.soft?.join(', '),
                (v) => handleChange('skills', 'soft', v.split(',').map(s => s.trim())), true)}
              {renderField('Certifications', editedData.skills.certifications?.join(', '),
                (v) => handleChange('skills', 'certifications', v.split(',').map(s => s.trim())), true)}
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      {!isFinalized && hasChanges && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving Changes...' : 'Save Corrections'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ResumeDataEditor;
